/**
 * The chat widget, drawn as a static document for the merchant app's Brand preview (MAR-391).
 *
 * This is NOT part of the shopper bundle (`scripts/build.js` bundles from `src/sdk/index.js`;
 * nothing there imports this). It lives beside the widget so the preview is built from the
 * widget's own pieces and cannot drift into an invented look:
 *
 * - the widget's own stylesheets, `CHAT_WIDGET_CSS` + `AFTER_HOURS_CSS`, in the order the SDK
 *   injects them;
 * - the merchant theme as the widget applies it (`merchantThemeCss`, the one builder
 *   `MarvaChat.applyMerchantTheme` calls), from the map `@marva/shared/theme-resolver` resolves
 *   — the caller resolves it, so this module stays free of `@marva/shared`;
 * - the proactive prompt card's markup, a copy of `MarvaChat.proactivePromptHtml` that
 *   `chat-preview.test.js` pins against the real method, so a markup change there fails here;
 * - the After Hours launcher's markup, from `../sdk/chat-launcher` — the module the widget itself
 *   builds the bubble and its launcher reskin from. `chat-launcher.test.js` proves the string
 *   parses to the DOM the live widget leaves.
 *
 * The page is `inert`: the preview is a picture of the widget, not a second input.
 *
 * What it does not claim: the words are a sample (her real messages are her own), and the site
 * behind it is a plain light or dark page — the widget picks `mv-light` / `mv-dark` from the
 * storefront's background (`MarvaChat.detectTheme`), which the preview can only offer as a choice.
 *
 * @module @marva/embed-script/src/preview/chat-preview
 */

'use strict';

const { AFTER_HOURS_CSS, CHAT_WIDGET_CSS, merchantThemeCss } = require('../sdk/chat-styles');
const { escapeHtml } = require('../sdk/html-safety');
const { afterHoursLauncherHtml } = require('../sdk/chat-launcher');

const DEFAULT_ENGAGE_LABEL = 'I need help';
const DEFAULT_DISMISS_LABEL = 'I\'m just browsing';
const SAMPLE_MESSAGE = 'Looking for something in particular? I can help you compare.';

/** The page behind the widget, and the theme class the widget would pick on it. */
const SITES = Object.freeze({
  light: Object.freeze({ themeClass: 'mv-light', background: '#FFFFFF' }),
  dark: Object.freeze({ themeClass: 'mv-dark', background: '#151412' })
});

/** `MarvaChat.proactivePromptHtml`, as a pure function of the name and labels. */
function proactivePromptHtml(assistantName, { message, engageLabel, dismissLabel }) {
  const name = escapeHtml(assistantName);
  return `
        <button class="marva-nudge-x" aria-label="Dismiss">&times;</button>
        <div class="marva-nudge-head"><div class="w-orb"></div><span class="marva-nudge-name">${name}</span></div>
        <div class="marva-nudge-msg">${escapeHtml(message)}</div>
        <div class="marva-nudge-actions">
          <button class="marva-nudge-go">${escapeHtml(engageLabel || DEFAULT_ENGAGE_LABEL)}</button>
          <button class="marva-nudge-no">${escapeHtml(dismissLabel || DEFAULT_DISMISS_LABEL)}</button>
        </div>`;
}

/**
 * A complete HTML document: a plain page with the widget's prompt card above its launcher.
 *
 * @param {Object} [opts]
 * @param {Object<string,string>|null} [opts.theme] - `resolveOnSiteTheme(...)`; null keeps the
 *   widget's own look, exactly as the SDK does.
 * @param {'light'|'dark'} [opts.site='light']
 * @param {string} [opts.assistantName='Marva']
 * @param {string} [opts.message] - defaults to a sample line.
 * @returns {string}
 */
function chatPreviewDocument({ theme = null, site = 'light', assistantName, message = SAMPLE_MESSAGE } = {}) {
  const page = SITES[site] || SITES.light;
  const name = (typeof assistantName === 'string' && assistantName.trim()) || 'Marva';
  const themeCss = merchantThemeCss(theme);
  return '<!doctype html><html lang="en"><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    `<style>${CHAT_WIDGET_CSS}</style>` +
    `<style>${AFTER_HOURS_CSS}</style>` +
    (themeCss ? `<style id="marva-merchant-theme">${themeCss}</style>` : '') +
    // The page itself, and the card shown settled rather than mid-transition.
    `<style>html,body{margin:0;height:100%;background:${page.background}}.marva-nudge{transition:none}</style>` +
    '</head><body inert>' +
    `<div class="marva-nudge ${page.themeClass} marva-afterhours in">` +
    proactivePromptHtml(name, { message }) +
    '</div>' +
    afterHoursLauncherHtml({ themeClass: page.themeClass, assistantName: name }) +
    '</body></html>';
}

module.exports = { chatPreviewDocument, proactivePromptHtml, SAMPLE_MESSAGE, SITES };
